/**
 * @fileoverview Getting a chosen picture ready to send, written once. Every
 * screen that takes a picture (a plant's photo, a replacement, a bed's cover,
 * the tray, Google Photos, a seed packet, Today's own picture, the profile
 * photo) used to make its own PhotoProcessor and handle a failure its own way:
 * the profile refused a picture the processor could not shrink, and every other
 * screen sent the original. This is the one rule, the more careful of the two:
 * a picture the processor could not make ready is not sent, and the gardener is
 * told why in the processor's own sentence.
 *
 * The work is PhotoProcessor's (an iPhone HEIC made a JPEG, the type checked,
 * the date read from the original's EXIF, a heavy or over-wide file shrunk);
 * this only wraps it with that one handling of a refusal.
 *
 * Who uses it: usePhotoChoice (the photo upload dialog), EditPhotoDialog,
 * EditLocationDialog, TodayPhotoDialog, ProfilePhotoCard,
 * TrayUploader and GooglePhotosResume.
 *
 * No network: it reads and redraws the file in the browser.
 *
 * @module preparePhoto
 */

import { PhotoProcessor } from '@/shared/services/PhotoProcessor';
import type { DateTime } from '@/shared/utils/dateUtils';
import { t } from '@/shared/i18n/i18nSetup';

/** What was learnt from a picture's EXIF on the way (read from the file as chosen). */
export interface PhotoExif {
  /** The day it was taken, floored: what a photo's date field is set from. */
  dateTaken?: DateTime;
  /** The moment it was taken, to the second, for telling one pile from the next. */
  takenAt?: DateTime;
}

/** A picture ready to send, or the reason it must not be. */
export type PreparedPhoto =
  | {
    /** The picture is ready. */
    ok: true;
    /** The file to send: the original, or a JPEG of it (converted from HEIC, shrunk, or both). */
    file: File;
    /** The day and moment its EXIF gave, when it had any. */
    exif: PhotoExif;
  }
  | {
    /** The picture must not be sent. */
    ok: false;
    /** A sentence for the gardener saying why (in their language). */
    message: string;
  };

/** The one processor: it holds no state, so every screen shares it. */
const processor = new PhotoProcessor();

/**
 * Gets a chosen picture ready to send, the same way on every screen.
 *
 * @param file - The picture as chosen (camera, file picker or Google Photos).
 * @returns Ready (the file to send and its EXIF dates), or refused with a sentence to show. Never throws.
 */
export async function preparePhoto(file: File): Promise<PreparedPhoto> {
  try {
    const result = await processor.processPhoto(file);
    if (result.success && result.data) return { ok: true, file: result.data.file, exif: result.data.exifData ?? {} };
    return { ok: false, message: result.error?.message || t('plants.photo.processFailed') };
  } catch {
    // PhotoProcessor catches and logs its own failures; this is a failure it did not expect.
    return { ok: false, message: t('plants.photo.processFailed') };
  }
}
